import {
  CardContentProps as MuiCardContentProps,
  CardContent as MuiCardContent,
  useThemeProps,
  SxProps,
  Theme,
} from '@mui/material';
import React from 'react';
import {
  cardContentHorizontalThemeSpacing,
  cardContentVerticalThemeSpacing,
} from 'src/components/CardContent/cardContentStyleOverrides';

export interface CardContentProps extends MuiCardContentProps {
  /**
   * Set to remove the top spacing.
   * @default false
   */
  disableTopSpacing?: boolean;
}

/**
 * CardContent wraps the main content area of a Card.
 *
 * Demos:
 *
 * - [Card — Content](https://unify.workiva.org/components/card?tab=code#card-content)
 *
 * API:
 *
 * - [CardContent API](https://unify.workiva.org/components/card?tab=props#cardcontent)
 */
export const CardContent = React.forwardRef<HTMLElement, CardContentProps>(
  function UnifyCardContent(inProps, ref) {
    const props = useThemeProps({ props: inProps, name: 'MuiCardContent' });

    const {
      disableTopSpacing,
      sx: inSx,
      children,
      component = 'div',
      ...rest
    } = props;

    // Apply style overrides to the `sx` props.
    const sx: SxProps<Theme> = {
      // Conditionally remove top spacing
      padding: (theme) =>
        theme.spacing(
          disableTopSpacing ? 0 : cardContentVerticalThemeSpacing,
          cardContentHorizontalThemeSpacing,
          cardContentVerticalThemeSpacing,
          cardContentHorizontalThemeSpacing
        ),
      ...inSx,
    };

    return (
      <MuiCardContent ref={ref} sx={sx} component={component} {...rest}>
        {children}
      </MuiCardContent>
    );
  }
);

export default CardContent;
